import { useEffect, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import AppSidebar from "./AppSidebar";
import AppTopbar from "./AppTopbar";
import type { AppNavGroup, AppUser } from "@/config/appNav";
import { NotificationsProvider } from "@/store/notifications/NotificationsProvider";
import { cn } from "@/utils/cn";

const SIDEBAR_KEY = "owner-sidebar-collapsed";

interface AppShellProps {
  groups: AppNavGroup[];
  user: AppUser;
  spaceLabel: string;
  fallbackTitle: string;
  accountLinks: { label: string; to: string; icon: string }[];
  planLabel?: string;
  planHint?: string;
  planTo?: string;
  notice?: ReactNode;
}

export default function AppShell({
  groups,
  user,
  spaceLabel,
  fallbackTitle,
  accountLinks,
  planLabel,
  planHint,
  planTo,
  notice,
}: AppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SIDEBAR_KEY) === "true";
  });
  const { pathname } = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_KEY, String(collapsed));
  }, [collapsed]);

  const navItems = groups.flatMap((group) => group.items);

  const title =
    navItems.find((item) => item.to === pathname)?.label ??
    navItems
      .filter((item) => item.to !== "/proprietaire" && pathname.startsWith(`${item.to}/`))
      .sort((a, b) => b.to.length - a.to.length)[0]?.label ??
    fallbackTitle;

  return (
    <NotificationsProvider>
    <div className="min-h-screen overflow-x-clip bg-background-100">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 hidden border-r border-foreground-900 transition-[width] duration-300 ease-in-out lg:block",
          collapsed ? "w-20" : "w-64"
        )}
      >
        <AppSidebar
          groups={groups}
          user={user}
          planLabel={planLabel}
          planHint={planHint}
          planTo={planTo}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((value) => !value)}
        />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40 animate-fade-in"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85%] border-r border-foreground-900 bg-foreground-950 animate-slide-in-left">
            <AppSidebar
              groups={groups}
              user={user}
              planLabel={planLabel}
              planHint={planHint}
              planTo={planTo}
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </div>
      )}

      <div
        className={cn(
          "flex min-h-screen flex-col transition-[padding] duration-300 ease-in-out",
          collapsed ? "lg:pl-20" : "lg:pl-64"
        )}
      >
        <AppTopbar
          title={title}
          spaceLabel={spaceLabel}
          user={user}
          onOpenSidebar={() => setMobileOpen(true)}
          accountLinks={accountLinks}
        />
        {notice}
        <main className="flex-1 px-4 py-5 md:px-6 md:py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
    </NotificationsProvider>
  );
}